import { Filter, Search, X } from 'lucide-react';
import { useMemo, useState, type ReactNode } from 'react';

/**
 * Finding rows in a list: a search box, a few chips, and an advanced drawer.
 *
 * ## Why one component rather than a filter bar per page
 *
 * Every list in this application needs the same three things and had none of
 * them consistently: a quick search, one or two obvious narrowings, and a way
 * to ask a question nobody anticipated. Written per page they drift — one
 * searches as you type and another waits for Enter, one keeps its filters when
 * you come back and another forgets — and the drift is what makes a system
 * feel unpredictable rather than merely limited.
 *
 * ## Why filtering happens here and not on the server
 *
 * These lists are the size of one business's records: dozens of meters,
 * hundreds of invoices, thousands of tickets at the outside. All of it is
 * already in the browser because the table renders it. Filtering in memory is
 * instant, works with no connection, and — the part that matters — cannot
 * disagree with what the table is showing, which a server filter and a client
 * sort eventually will.
 *
 * The day a list outgrows that, the fix is paging the query and moving these
 * same predicates behind it. `fields` is deliberately a description of the
 * question rather than a closure over the rows, so that move is possible.
 *
 * ## The advanced drawer is not a query builder
 *
 * It is field, comparison, value — three dropdowns and a box, joined by AND.
 * Anything richer (OR, brackets, saved queries) is a language, and a language
 * needs teaching. What people actually ask of a list is "show me the overdue
 * ones from this month", and three clauses of AND covers it.
 */

export type FilterField<Row> = {
  /** Stable, and what a saved filter would store. */
  key: string;
  label: string;
  /** What kind of comparison makes sense, and how the value is entered. */
  type: 'text' | 'number' | 'date' | 'choice';
  /** For `choice`. The value stored, and what is shown for it. */
  options?: Array<{ value: string; label: string }>;
  /**
   * The value to compare against, taken from the row.
   *
   * Dates come back as an ISO string or a timestamp; numbers as numbers. A
   * `null` means the row has nothing to compare, and never matches — an empty
   * cell is the absence of a value, not a small one.
   */
  value: (row: Row) => string | number | null;
  /** Also searched by the quick box. Default true for text fields. */
  quick?: boolean;
};

type Comparison = 'is' | 'is not' | 'contains' | 'before' | 'after' | 'at least' | 'at most';

/** Which comparisons make sense for each kind of field. */
const COMPARISONS: Record<FilterField<unknown>['type'], Comparison[]> = {
  text: ['contains', 'is', 'is not'],
  choice: ['is', 'is not'],
  number: ['is', 'at least', 'at most'],
  date: ['before', 'after', 'is'],
};

export type Clause = { field: string; comparison: Comparison; value: string };

/** One clause, applied to one row. */
function matches<Row>(row: Row, clause: Clause, fields: FilterField<Row>[]): boolean {
  const field = fields.find((one) => one.key === clause.field);
  if (!field || !clause.value.trim()) return true;

  const actual = field.value(row);
  if (actual === null) return false;

  const wanted = clause.value.trim();

  if (field.type === 'date') {
    const at = new Date(actual).getTime();
    const against = new Date(wanted).getTime();
    if (Number.isNaN(at) || Number.isNaN(against)) return true;
    if (clause.comparison === 'before') return at < against;
    if (clause.comparison === 'after') return at > against;
    /*
     * "is" on a date means that calendar day, not that millisecond. Nobody
     * asking for invoices dated the 3rd means the instant midnight began.
     */
    return new Date(at).toDateString() === new Date(against).toDateString();
  }

  if (field.type === 'number') {
    const at = Number(actual);
    const against = Number(wanted);
    if (Number.isNaN(against)) return true;
    if (clause.comparison === 'at least') return at >= against;
    if (clause.comparison === 'at most') return at <= against;
    return at === against;
  }

  const text = String(actual).toLowerCase();
  const against = wanted.toLowerCase();
  if (clause.comparison === 'contains') return text.includes(against);
  if (clause.comparison === 'is not') return text !== against;
  return text === against;
}

export function useFilters<Row>(rows: Row[], fields: FilterField<Row>[]) {
  const [query, setQuery] = useState('');
  const [clauses, setClauses] = useState<Clause[]>([]);

  const filtered = useMemo(() => {
    const needle = query.trim().toLowerCase();

    return rows.filter((row) => {
      /*
       * The quick box searches every field that says it is quick-searchable,
       * which is what somebody expects of a search box on a list: they type a
       * name, or half a code, or a telephone number, without first choosing
       * which of those they are typing.
       */
      if (needle) {
        const hit = fields
          .filter((field) => field.quick ?? field.type === 'text')
          .some((field) => String(field.value(row) ?? '').toLowerCase().includes(needle));
        if (!hit) return false;
      }

      // Joined by AND: each clause narrows what the last one left.
      return clauses.every((clause) => matches(row, clause, fields));
    });
  }, [rows, fields, query, clauses]);

  return { query, setQuery, clauses, setClauses, filtered };
}

export function Filters<Row>({
  fields,
  query,
  onQuery,
  clauses,
  onClauses,
  total,
  showing,
  placeholder = 'Search this list',
  children,
}: {
  fields: FilterField<Row>[];
  query: string;
  onQuery: (value: string) => void;
  clauses: Clause[];
  onClauses: (clauses: Clause[]) => void;
  total: number;
  showing: number;
  placeholder?: string;
  /** Page-specific controls that belong beside the search — a status tab, say. */
  children?: ReactNode;
}) {
  const [open, setOpen] = useState(false);

  const add = () => {
    const first = fields[0];
    if (!first) return;
    onClauses([
      ...clauses,
      { field: first.key, comparison: COMPARISONS[first.type][0]!, value: '' },
    ]);
    setOpen(true);
  };

  const update = (index: number, patch: Partial<Clause>) =>
    onClauses(clauses.map((clause, at) => (at === index ? { ...clause, ...patch } : clause)));

  const drop = (index: number) => onClauses(clauses.filter((_, at) => at !== index));

  const active = clauses.filter((clause) => clause.value.trim()).length;

  return (
    <div className="space-y-2 print:hidden">
      <div className="flex flex-wrap items-center gap-2">
        <div className="relative min-w-0 flex-1">
          <Search
            className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground"
            aria-hidden="true"
          />
          <input
            type="search"
            className="input w-full pl-9"
            placeholder={placeholder}
            value={query}
            onChange={(event) => onQuery(event.target.value)}
          />
        </div>

        {children}

        <button
          type="button"
          className="btn-secondary shrink-0"
          aria-expanded={open}
          onClick={() => (clauses.length === 0 ? add() : setOpen((was) => !was))}
        >
          <Filter className="h-4 w-4" aria-hidden="true" />
          Filters
          {active > 0 && (
            <span className="ml-1 rounded-full bg-primary-600 px-1.5 text-xs text-white">
              {active}
            </span>
          )}
        </button>
      </div>

      {open && (
        <div className="space-y-2 rounded-xl border border-border p-3">
          {clauses.length === 0 && (
            <p className="text-sm text-muted-foreground">
              No filters yet. Add one to narrow the list by a particular field.
            </p>
          )}

          {clauses.map((clause, index) => {
            const field = fields.find((one) => one.key === clause.field) ?? fields[0]!;
            return (
              <div key={index} className="flex flex-wrap items-center gap-2">
                <select
                  className="input w-auto min-w-[9rem]"
                  aria-label="Field"
                  value={clause.field}
                  onChange={(event) => {
                    const next = fields.find((one) => one.key === event.target.value)!;
                    /*
                     * The comparison resets with the field. "contains" makes no
                     * sense on a date, and leaving a stale one there produces a
                     * filter that silently matches everything.
                     */
                    update(index, {
                      field: next.key,
                      comparison: COMPARISONS[next.type][0]!,
                      value: '',
                    });
                  }}
                >
                  {fields.map((one) => (
                    <option key={one.key} value={one.key}>
                      {one.label}
                    </option>
                  ))}
                </select>

                <select
                  className="input w-auto"
                  aria-label="Comparison"
                  value={clause.comparison}
                  onChange={(event) =>
                    update(index, { comparison: event.target.value as Comparison })
                  }
                >
                  {COMPARISONS[field.type].map((one) => (
                    <option key={one} value={one}>
                      {one}
                    </option>
                  ))}
                </select>

                {field.type === 'choice' ? (
                  <select
                    className="input w-auto min-w-[9rem]"
                    aria-label="Value"
                    value={clause.value}
                    onChange={(event) => update(index, { value: event.target.value })}
                  >
                    <option value="">Choose…</option>
                    {(field.options ?? []).map((one) => (
                      <option key={one.value} value={one.value}>
                        {one.label}
                      </option>
                    ))}
                  </select>
                ) : (
                  <input
                    className="input w-auto min-w-[10rem]"
                    type={field.type === 'date' ? 'date' : field.type === 'number' ? 'number' : 'text'}
                    aria-label="Value"
                    value={clause.value}
                    onChange={(event) => update(index, { value: event.target.value })}
                  />
                )}

                <button
                  type="button"
                  className="rounded-lg p-2 text-muted-foreground hover:bg-muted"
                  title="Remove this filter"
                  onClick={() => drop(index)}
                >
                  <X className="h-4 w-4" aria-hidden="true" />
                  <span className="sr-only">Remove this filter</span>
                </button>
              </div>
            );
          })}

          <div className="flex flex-wrap gap-2 border-t border-border pt-2">
            <button type="button" className="btn-secondary py-1.5 text-sm" onClick={add}>
              Add a filter
            </button>
            {clauses.length > 0 && (
              <button
                type="button"
                className="btn-ghost py-1.5 text-sm"
                onClick={() => onClauses([])}
              >
                Clear them all
              </button>
            )}
          </div>
        </div>
      )}

      {/*
        * Said out loud, always.
        *
        * A filtered list that does not say it is filtered is the oldest trap in
        * this kind of screen: somebody narrows it, forgets, and reports that
        * records have gone missing.
        */}
      {(showing !== total || active > 0 || query.trim()) && (
        <p className="text-xs text-muted-foreground">
          Showing {showing} of {total}.{' '}
          {(active > 0 || query.trim()) && (
            <button
              type="button"
              className="underline underline-offset-2 hover:text-foreground"
              onClick={() => {
                onQuery('');
                onClauses([]);
              }}
            >
              Show everything
            </button>
          )}
        </p>
      )}
    </div>
  );
}
